import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TimeAgoPipe } from '@ui-lib/pipes';
import { ENVIRONMENT, PIMSEnvironment } from "environments";
import { Observable, Subscription } from 'rxjs';
import { GpAddressesApiService } from '../../../api/admin/orders/gpaddresses/gp-addresses.service';
import { GpLettersService } from '../../../services/gp-letters/gp-letters.service';
import { GpAddressResponse, QueryParams, QueryParamsFilter } from '../../../types/gp-address-request-response';
import { GpAddressStatus, GpAddressVerifiedStatus, IncompleteGpAddressTableData } from '../../../types/gp-letters';
import { FollowupComponent } from '../components/buttons/followup/followup.component';
import { StatusUpdateComponent } from '../components/buttons/status-update/status-update.component';
import { EmitFilterChangeParams, FiltersComponent } from '../components/filters/filters.component';

@Component({
    selector: 'unresponsive-patients',
    imports: [
      // TODO: Angular 19 WARNINGS: Unused import. Delete if unnecessary
      // MatFormField,
      // MatLabel,
      // MatSelect,
      // MatOption,
      // MultiSelectComponent,
      // MedicationFilterComponent,
      //_______________________
      CommonModule,
      MatTableModule,
      MatPaginatorModule,
      FormsModule,
      ReactiveFormsModule,
      FollowupComponent,
      StatusUpdateComponent,
      TimeAgoPipe,
      FiltersComponent,
    ],
    templateUrl: './unresponsive-patients.component.html',
    styleUrl: './unresponsive-patients.component.scss'
})
export class UnresponsivePatientsComponent implements OnDestroy {
  private gpAddressesApiService = inject(GpAddressesApiService);
  private gpLettersService = inject(GpLettersService);
  private ref = inject(ChangeDetectorRef);

  private env = inject<PIMSEnvironment>(ENVIRONMENT)
  displayedColumns: string[] = [
    'visit_id',
    'service',
    'medications',
    'surgery_name',
    'line_1',
    'line_2',
    'line_3',
    'town',
    'postcode',
    'created',
    'noOfContacts',
    'lastContacted',
    'edit',
    'follow up',
    'verify',
  ];
  dataSource = new MatTableDataSource<IncompleteGpAddressTableData>([]);
  searchTerm = '';
  paginator: PageEvent = { pageIndex: 0, pageSize: 20, length: 0 };
  pageSizeOptions = [10, 20, 50, 75, 100];
  filter: QueryParamsFilter = {};
  protected readonly GpAddressStatus = GpAddressStatus;
  protected readonly GpAddressVerifiedStatus = GpAddressVerifiedStatus;
  private subs = new Subscription();

  onFilterChanged({ filter, searchTerm }: EmitFilterChangeParams) {
    this.searchTerm = searchTerm;
    this.filter = { ...filter };
    this.getAddresses(this.paginator, filter, searchTerm);
  }

  handlePageEvent(event: PageEvent) {
    this.paginator = { ...event };
    this.getAddresses(this.paginator, this.filter, this.searchTerm);
  }

  private getAddresses(paginator: PageEvent, filter: QueryParamsFilter, search?: string) {
    const queryParams: QueryParams = {
      filter: {
        ...filter,
      },
      page: {
        number: paginator.pageIndex + 1,
        limit: paginator.pageSize,
      },
      search: search,
      cache: 0,
    };
    this.subs.add(
      this.getAddresses$(queryParams).subscribe(response => {
        this.handleResponse(response);
      }),
    );
  }

  private getAddresses$(queryParams: QueryParams): Observable<GpAddressResponse> {
    return this.gpAddressesApiService.getAddresses(queryParams);
  }

  private handleResponse(response: GpAddressResponse) {
    this.dataSource = new MatTableDataSource<IncompleteGpAddressTableData, MatPaginator>(
      this.gpLettersService.transformJsonApiData(response),
    );
    this.paginator.length = response.meta.pagination.total;
    this.ref.markForCheck();
  }

  saveChanges(element: IncompleteGpAddressTableData) {
    this.subs.add(
      this.gpAddressesApiService.putUpdateAddresses$(element.clone.gpaddress_id, element.clone).subscribe(response => {
        if (response) {
          const { surgery_name, line_1, line_2, line_3, postcode, town } = { ...response?.data?.attributes };
          element.surgery_name = surgery_name;
          element.line_1 = line_1;
          element.line_2 = line_2;
          element.line_3 = line_3;
          element.postcode = postcode;
          element.town = town;
          element.editMode = false;
        }
      }),
    );
  }

  cancelEditMode(element: IncompleteGpAddressTableData) {
    element.clone = {
      ...element,
    };
    element.editMode = false;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
